import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ErrorResponse, User as OIDCUser, UserManager } from 'oidc-client-ts';
import { environment } from '../../environments/environment';
import { LoginResult } from './auth.models';
import { UserCredentials, UserProfile } from '@models/user';
import { DataContext, StorageService } from '@services';

const LOCALE_TO_LANGUAGE: Record<string, string> = {
  '1': 'no',
  '2': 'se',
  '3': 'en',
  '4': 'da',
};

interface UserInfoResponse {
  sub: string;
  email?: string;
  name?: string;
  locale?: string;
}

@Service()
export class AuthService {
  private readonly httpClient = inject(HttpClient);
  private readonly storage = inject(StorageService);
  private readonly dataContext = inject(DataContext);

  private readonly userManager: UserManager;
  private oidcUser: OIDCUser | null = null;

  constructor() {
    this.userManager = new UserManager({
      authority: environment.authSettings.authority,
      client_id: environment.authSettings.clientId,
      client_secret: environment.authSettings.clientSecret,
      response_type: 'id_token token',
      prompt: 'login',
      scope: environment.authSettings.scope,
      automaticSilentRenew: false,
      redirect_uri: '',
    });
  }

  async signin(username: string, password: string): Promise<LoginResult> {
    try {
      await this.userManager.signinResourceOwnerCredentials({ username, password });
      await this.loadUserInfo();
      await this.loadUserProfile();
      await this.loadLoggedInUser();
      await this.storage.setUserCredentials({ username, password });
      await this.storage.setRememberedEmail(username);
      return LoginResult.Success;
    } catch (error) {
      if (error instanceof ErrorResponse && error.error === 'invalid_grant') {
        return LoginResult.InvalidCredentials;
      }
      const err = error as { status?: number };
      if (!err?.status || err?.status === 0) {
        return LoginResult.NetworkError;
      }
      throw error;
    }
  }

  async loadUserInfo(): Promise<void> {
    this.oidcUser = await this.userManager.getUser();
  }

  async loadUserProfile(): Promise<void> {
    const userEndpoint = await this.userManager.metadataService.getUserInfoEndpoint();
    const userInfo = await firstValueFrom(this.httpClient.get<UserInfoResponse>(userEndpoint));
    const user: UserProfile = {
      id: userInfo.sub,
      email: userInfo.email ?? '',
      name: userInfo.name ?? '',
      locale: userInfo.locale,
    };
    await this.storage.setCurrentUser(user);
  }

  async getAccessToken(): Promise<string | undefined> {
    if (!this.oidcUser) {
      await this.loadUserInfo();
    }
    return this.oidcUser?.access_token ?? undefined;
  }

  async loadLoggedInUser(): Promise<void> {
    const user = await this.storage.getCurrentUser();
    if (user) {
      const language = LOCALE_TO_LANGUAGE[user.locale ?? '1'] ?? 'no';
      this.dataContext.setLoggedInUser({ id: user.id, name: user.name, email: user.email, language });
    }
  }

  async isAppAuthorized(): Promise<boolean> {
    const credentials: UserCredentials | null = await this.storage.getUserCredentials();
    const user: UserProfile | null = await this.storage.getCurrentUser();
    return !!credentials?.username && !!credentials?.password && !!user?.id;
  }

  async logout(): Promise<void> {
    await this.storage.removeUserCredentials();
    await this.storage.removeCurrentUser();
    this.oidcUser = null;
    this.dataContext.clearUser();
    this.dataContext.clearProjectContext();
  }
}
